import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CreditCard, ExternalLink, Check, AlertCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import styles from "./onboarding.module.css";

interface StripeStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

export const StripeStep = ({
	onNext,
	onBack,
	initialData,
}: StripeStepProps) => {
	const { user } = useAuth();
	const [connected, setConnected] = useState(!!initialData?.stripeAccountId);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [onboardingUrl, setOnboardingUrl] = useState<string>("");
	const [popupWindow, setPopupWindow] = useState<Window | null>(null);

	// Check for OAuth callback on component mount
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const stripeConnect = urlParams.get("stripe_connect");

		if (stripeConnect === "true") {
			// User returned from Stripe Connect onboarding
			checkStripeConnectStatus();
			// Clean up the URL
			window.history.replaceState({}, document.title, window.location.pathname);
		}
	}, []);

	// Check if user is connected to Stripe
	const checkStripeConnectStatus = async () => {
		try {
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-doula-onboarding-data`,
				{
					method: "GET",
					headers: {
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
				},
			);

			const result = await response.json();

			if (
				result.success &&
				result.data.doula_listing?.stripe_connect_account_id
			) {
				setConnected(true);
			}
		} catch (error) {
			console.error("Error checking Stripe Connect status:", error);
		}
	};

	const handleStripeConnect = async () => {
		setLoading(true);
		setError(null);

		try {
			// Check if user already has a Stripe account
			const checkResponse = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-doula-onboarding-data`,
				{
					method: "GET",
					headers: {
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
				},
			);

			const checkResult = await checkResponse.json();
			const existingAccountId = checkResult.success
				? checkResult.data.doula_listing?.stripe_connect_account_id
				: null;

			let requestBody;
			if (existingAccountId) {
				// If already connected, create a login link instead
				requestBody = {
					action: "create_login_link",
					accountId: existingAccountId,
				};
			} else {
				// Create new account
				requestBody = {
					action: "create_account",
					userId: user?.id,
					email: user?.email,
					returnUrl: `${window.location.origin}/stripe-callback.html`,
					refreshUrl: `${window.location.origin}/doula/onboarding?refresh=true`,
				};
			}

			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-connect-onboarding`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify(requestBody),
				},
			);

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to initiate Stripe Connect");
			}

			const stripeUrl = result.onboardingUrl || result.url;
			if (stripeUrl) {
				setOnboardingUrl(stripeUrl);

				// Open Stripe onboarding in popup window
				const popup = window.open(
					stripeUrl,
					"stripe-connect",
					"width=800,height=600,scrollbars=yes,resizable=yes",
				);

				if (popup) {
					setPopupWindow(popup);

					// Listen for message from popup
					const handleMessage = (event: MessageEvent) => {
						if (event.origin !== window.location.origin) return;

						if (event.data.type === "STRIPE_CONNECTED") {
							window.removeEventListener("message", handleMessage);
							popup.close();
							checkStripeConnectStatus();
						}
					};

					window.addEventListener("message", handleMessage);

					// Fallback: Check if popup is closed manually
					const checkPopup = setInterval(() => {
						try {
							if (popup.closed) {
								clearInterval(checkPopup);
								window.removeEventListener("message", handleMessage);
								checkStripeConnectStatus();
							}
						} catch (e) {
							// COOP may block access to popup.closed
							// This is expected and not an error
						}
					}, 1000);

					// Timeout after 5 minutes
					setTimeout(
						() => {
							clearInterval(checkPopup);
							window.removeEventListener("message", handleMessage);
							try {
								popup.close();
							} catch (e) {
								// Ignore COOP errors
							}
						},
						5 * 60 * 1000,
					);
				}
			} else {
				throw new Error("No Stripe URL received");
			}
		} catch (error) {
			console.error("Error connecting Stripe:", error);
			setError("Error connecting to Stripe. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async () => {
		if (!connected) {
			setError("Please connect your Stripe account first.");
			return;
		}

		try {
			setLoading(true);

			// Get current doula data to retrieve Stripe account ID
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-doula-onboarding-data`,
				{
					method: "GET",
					headers: {
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
				},
			);

			const result = await response.json();

			if (
				result.success &&
				result.data.doula_listing?.stripe_connect_account_id
			) {
				onNext({
					stripeAccountId: result.data.doula_listing.stripe_connect_account_id,
				});
			} else {
				throw new Error("Stripe account not found");
			}
		} catch (error) {
			console.error("Failed to save Stripe settings:", error);
			setError("Failed to save settings. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleSkip = () => {
		onNext({ stripeAccountId: "" });
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
			className={styles.wrapperNarrow}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "100%" }} />
				</div>
				<p className={styles.progressNote}>Step 14 of 15</p>
			</div>

			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<CreditCard className={styles.cardIcon} />
						Payment Processing Setup
					</CardTitle>
					<CardDescription className={styles.cardSubtitle}>
						Connect your Stripe account to securely receive payments from
						families.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{error && (
						<Alert variant="destructive">
							<AlertCircle className="h-4 w-4" />
							<AlertDescription className="font-karla">{error}</AlertDescription>
						</Alert>
					)}

					{!connected ? (
						<div className="text-center space-y-4">
							<div className={styles.innerPanelLg}>
								<img
									src="https://asxufbnomjttcikjcjfa.supabase.co/storage/v1/object/public/marketing/stripe_logo.jpg"
									alt="Stripe"
									className="w-24 h-auto mx-auto mb-4"
								/>
								<h3 className="text-lg font-semibold text-newTheme-black mb-2 font-karla">
									Connect Your Stripe Account
								</h3>
								<p className="text-newTheme-black/60 mb-6 font-karla">
									Stripe handles all payment processing securely and deposits
									funds directly to your bank account.
								</p>

								<Button
									onClick={handleStripeConnect}
									disabled={loading}
									className={`w-full mb-4 ${styles.submitButton}`}
									size="lg"
								>
									{loading ? (
										<>
											<div className={styles.spinner} />
											Connecting...
										</>
									) : (
										<>
											<ExternalLink className="h-4 w-4 mr-2" />
											Connect Stripe Account
										</>
									)}
								</Button>

								<div className={`mb-4 ${styles.infoAlert}`}>
									<h4 className="font-medium text-newTheme-spruce mb-2 font-karla">
										What you'll need:
									</h4>
									<ul className={`space-y-1 list-disc list-inside ${styles.infoText}`}>
										<li>Business or personal tax ID</li>
										<li>Bank account information</li>
										<li>Personal identification</li>
										<li>Business details (if applicable)</li>
									</ul>
								</div>

								<div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
									<h4 className="font-medium text-green-800 mb-2 font-karla">
										Benefits:
									</h4>
									<ul className="text-sm text-green-700 space-y-1 list-disc list-inside font-karla">
										<li>Secure payment processing</li>
										<li>Automatic deposits to your bank</li>
										<li>Transaction history and reporting</li>
										<li>Dispute protection</li>
									</ul>
								</div>
							</div>
						</div>
					) : (
						<div className="space-y-4">
							<div className="bg-green-50 border border-green-200 rounded-lg p-4">
								<div className="flex items-center gap-3">
									<Check className="h-5 w-5 text-green-600" />
									<div>
										<p className="font-medium text-newTheme-black font-karla">
											Stripe Account Connected
										</p>
										<p className="text-sm text-newTheme-black/60 font-karla">
											You're all set to receive payments from clients securely.
										</p>
									</div>
								</div>
							</div>

							<div className={styles.infoAlert}>
								<p className={styles.infoText}>
									<strong>Next steps:</strong> Once connected, you'll receive
									payments directly to your bank account. All transactions are
									secure and protected by Stripe's fraud detection.
								</p>
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			<div className={`${styles.navRow} mt-8`}>
				<Button
					variant="outline"
					onClick={onBack}
					className={styles.backButton}
				>
					Back
				</Button>
				<div className="flex gap-2">
					<Button
						variant="outline"
						onClick={handleSkip}
						className={styles.backButton}
					>
						Skip for Now
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!connected || loading}
						className={styles.submitButton}
					>
						{loading ? (
							<div className="flex items-center">
								<div className={styles.spinner} />
								Saving...
							</div>
						) : (
							"Continue"
						)}
					</Button>
				</div>
			</div>
		</motion.div>
	);
};
